import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Check } from "lucide-react";

const formatDateES = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function ConsentDialog({
  open,
  onOpenChange,
  course,
  student,
  onConfirm,
  submitting,
}) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [imageDenied, setImageDenied] = useState(false);

  const handleConfirm = () => {
    if (!acknowledged) return;
    onConfirm({
      consent_accepted: true,
      image_consent_denied: imageDenied,
    });
  };

  const handleOpenChange = (v) => {
    if (!v) {
      setAcknowledged(false);
      setImageDenied(false);
    }
    onOpenChange(v);
  };

  // Format schedule from course data
  const sched1 =
    course?.time_start_day1 && course?.time_end_day1
      ? `${course.time_start_day1} a ${course.time_end_day1}`
      : null;
  const sched2 =
    course?.time_start_day2 && course?.time_end_day2
      ? `${course.time_start_day2} a ${course.time_end_day2}`
      : null;
  const sched3 =
    Number(course?.days) >= 3 && course?.time_start_day3 && course?.time_end_day3
      ? `${course.time_start_day3} a ${course.time_end_day3}`
      : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[92vh] p-0 overflow-hidden flex flex-col"
        data-testid="consent-dialog"
      >
        <DialogHeader className="p-6 pb-3 border-b border-zinc-200 shrink-0">
          <DialogTitle className="heading-font text-2xl">
            Ficha de inscripción
          </DialogTitle>
          <DialogDescription>
            Por favor, lee y confirma para finalizar tu asistencia.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-scroll px-6 py-4 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-track]:bg-zinc-100 [&::-webkit-scrollbar-thumb]:bg-zinc-400 [&::-webkit-scrollbar-thumb]:rounded-full"
          data-testid="consent-scroll-area"
        >
          <div className="space-y-5 text-sm text-zinc-800 leading-relaxed pr-3">
            <section>
              <h3 className="heading-font text-base font-bold text-zinc-950 mb-2">
                Datos de la acción formativa
              </h3>
              <ul className="space-y-1">
                <li>
                  <span className="font-semibold">Acción formativa:</span>{" "}
                  {course?.name || course?.client_name || "—"}
                </li>
                <li>
                  <span className="font-semibold">Lugar de impartición:</span>{" "}
                  {course?.address || "—"}
                  {course?.city ? `, ${course.city}` : ""}
                </li>
                <li>
                  <span className="font-semibold">Fecha:</span>{" "}
                  {[
                    course?.date1,
                    Number(course?.days) >= 2 ? course?.date2 : null,
                    Number(course?.days) >= 3 ? course?.date3 : null,
                  ]
                    .filter(Boolean)
                    .map(formatDateES)
                    .join(" · ")}
                </li>
                {(sched1 || sched2 || sched3) && (
                  <li>
                    <span className="font-semibold">Horario:</span>{" "}
                    {[sched1, sched2, sched3].filter(Boolean).join(" · ")}
                  </li>
                )}
              </ul>
            </section>

            <section className="border-t border-zinc-200 pt-4">
              <h3 className="heading-font text-base font-bold text-zinc-950 mb-2">
                Cláusula informativa RGPD
              </h3>
              <p>
                Esta ficha de inscripción está creada para cumplir con el
                Reglamento General de Protección de Datos de nuestra empresa.
                En ningún caso vamos a realizar actividades comerciales con sus
                datos, sus datos no van a ser cedidos a terceros, ni le vamos a
                enviar información comercial. Sus datos son necesarios para
                realizar los certificados de la formación y las gestiones con
                la Consejería de Sanidad de la Comunidad Autónoma y por si
                necesitamos alguna aclaración relacionada con la acción
                formativa.
              </p>
            </section>

            <section className="border-t border-zinc-200 pt-4">
              <h3 className="heading-font text-base font-bold text-zinc-950 mb-2">
                Tus datos
              </h3>
              <ul className="space-y-1">
                <li>
                  <span className="font-semibold">Nombre y apellidos:</span>{" "}
                  {student?.name} {student?.surname}
                </li>
                <li>
                  <span className="font-semibold">DNI:</span>{" "}
                  {student?.dni || "—"}
                </li>
              </ul>
            </section>

            <section className="border-t border-zinc-200 pt-4">
              <h3 className="heading-font text-base font-bold text-zinc-950 mb-2">
                Consentimiento y tratamiento de datos
              </h3>
              <p>
                Le informamos conforme a lo previsto en el RGPD de 27 de abril
                de 2016 que <strong>DESFIEMER S.L.</strong> recaba y trata sus
                datos de carácter personal, aplicando las medidas técnicas y
                organizativas que garantizan su confidencialidad, con la
                finalidad de gestionar la relación que le vincula. Usted da su
                consentimiento y autorización para dicho tratamiento.
                Conservaremos sus datos de carácter personal únicamente el
                tiempo imprescindible para gestionar nuestra relación.
              </p>
              <p className="mt-3">
                Podrá ejercitar los derechos de acceso, rectificación,
                supresión, limitación, portabilidad y oposición dirigiéndose a{" "}
                <strong>DESFIEMER S.L.</strong> con dirección C/ Ciudad de
                Toledo 1, local 29, 05004, Ávila (ÁVILA).
              </p>
            </section>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-zinc-200 bg-zinc-50 space-y-3 shrink-0">
          <label
            htmlFor="consent-acknowledged"
            className="flex items-start gap-3 cursor-pointer"
            data-testid="consent-acknowledged-label"
          >
            <Checkbox
              id="consent-acknowledged"
              checked={acknowledged}
              onCheckedChange={(v) => setAcknowledged(!!v)}
              className="mt-0.5 h-5 w-5 border-2 border-zinc-300 data-[state=checked]:bg-zinc-950 data-[state=checked]:border-zinc-950"
              data-testid="consent-acknowledged-checkbox"
            />
            <span className="text-sm font-semibold text-zinc-950 leading-snug">
              HE LEÍDO Y ESTOY DE ACUERDO
            </span>
          </label>

          <label
            htmlFor="consent-image-denied"
            className="flex items-start gap-3 cursor-pointer"
            data-testid="consent-image-denied-label"
          >
            <Checkbox
              id="consent-image-denied"
              checked={imageDenied}
              onCheckedChange={(v) => setImageDenied(!!v)}
              className="mt-0.5 h-5 w-5 border-2 border-zinc-300 data-[state=checked]:bg-zinc-950 data-[state=checked]:border-zinc-950"
              data-testid="consent-image-denied-checkbox"
            />
            <span className="text-sm text-zinc-800 leading-snug">
              Marque esta casilla si <strong>NO QUIERE</strong> que{" "}
              <strong>Desfiemer S.L.</strong> utilice su imagen en sus Redes
              Sociales y Blog de su página web{" "}
              <span className="text-zinc-700 underline">www.desfiemer.com</span>
            </span>
          </label>
        </div>

        <DialogFooter className="p-4 border-t border-zinc-200 bg-white gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
            className="border border-zinc-300"
            data-testid="consent-cancel-button"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!acknowledged || submitting}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="consent-confirm-button"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Enviando…
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Confirmar y enviar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
